import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { GpsInlineLoader } from "@/components/gps-loader";
import {
  useVehicles,
  useVehicleImages,
  useVehicleMetadataSummaries,
  useVehicleSearchIndex,
  useDeviceNotifications,
  useToggleDeviceNotification,
  useQuickToggleRules,
  useQuickToggleRule,
} from "@/hooks/queries";
import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import type { Vehicle } from "@/types";
import { WaitingBackend } from "@/components/waiting-backend";
import { VehicleFormDialog, VehicleEditDialog } from "@/routes/index";
import { VehicleTrashDialog } from "@/components/vehicle-trash-dialog";
import { VehicleBulkCreateDialog } from "@/components/vehicle-bulk-create-dialog";
import { VehicleAccessDialog } from "@/components/vehicle-access-dialog";
import { VehicleSendCommandDialog } from "@/components/vehicle-send-command-dialog";
import { VehicleExpandedMap } from "@/components/vehicle-expanded-map";
import { VehicleLogsPanel } from "@/components/vehicle-logs-panel";
import { resolveVehicleIcon } from "@/lib/vehicle-icons";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { usePermission, usePermissionGateStatus } from "@/hooks/use-permission";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import { matchesVehicleSearch, vehicleSearchHaystack } from "@/lib/vehicle-search";
import { Loader2, AlertTriangle } from "lucide-react";
import { ALERT_CATALOG } from "@/lib/alert-types";
import { AlertLimitsEditor, isLimitEditable, limitSummary } from "@/components/alert-limits-editor";
import type { AlertRuleConfig } from "@/services/alert-rules.service";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { isRestrictedAccountType } from "@/lib/account-type";
import { LicensePlate } from "@/components/license-plate";
import { agoLabel } from "@/lib/time-ago";

// Pedido 2026-09-01: no painel expandido da linha (seta ao lado do lápis),
// "Históricos" saiu (redundante com o Centro de Histórico já real em
// /vehicles → seta de rastreamento, ver route_history_center) e "Alertas"
// entrou — "Logs" também passou a mostrar dado real (antes as duas eram
// pills mortas, só mudavam de cor ao clicar, sem trocar nenhum conteúdo).
// VehicleLogsPanel (feed de eventos reais do Traccar) foi extraído pra
// components/vehicle-logs-panel.tsx em 2026-09-08 pra ser reaproveitado
// também na aba Logs de Editar Veículo.
type ExpandTab = "acessos" | "comandos" | "logs" | "alertas" | "relatorios";

// Alertas do veículo — pedido 2026-09-01: "quero o design igual e quando
// entro no mapa e clico em cima do veículo e vou em alertar ou
// notificações" — a TabAlertas do mapa (lista crua dos tipos de
// notificação que o Traccar devolve) já estava desatualizada frente ao
// redesign de 2026-08-31 (ALERT_CATALOG: 2 grupos com cabeçalho, emoji,
// descrição em PT-BR, "Em breve" pros que ainda não existem) — esse
// redesign só tinha chegado no app PWA (MobileNotificationsPanel, ver
// mobile-app.tsx), nunca no desktop. Aqui reaproveita exatamente o mesmo
// catálogo/lógica de toggle do PWA, só que com o visual claro/tabela do
// resto desta página (a PWA é escura por padrão, não dá pra copiar 1:1).
function VehicleAlertsPanel({ vehicle }: { vehicle: Vehicle }) {
  const deviceId = Number(vehicle.id);
  const notifQuery = useDeviceNotifications(deviceId);
  const toggleMutation = useToggleDeviceNotification(deviceId);
  const ruleQuery = useQuickToggleRules(deviceId);
  const ruleToggleMutation = useQuickToggleRule(deviceId);
  const [pendingType, setPendingType] = useState<string | null>(null);

  const enabledTypes = useMemo(
    () => new Set((notifQuery.data ?? []).map((n) => n.type)),
    [notifQuery.data],
  );
  const enabledRuleTypes = useMemo(
    () => new Set((ruleQuery.data ?? []).filter((r) => r.enabled).map((r) => r.type)),
    [ruleQuery.data],
  );

  const handleToggle = (types: string[], next: boolean) => {
    setPendingType(types[0]);
    Promise.all(types.map((type) => toggleMutation.mutateAsync({ type, enabled: next })))
      .then(() => {
        toast.success(next ? "Alerta ativado" : "Alerta desativado");
      })
      .catch((err) => {
        toast.error("Não foi possível salvar o alerta", {
          description: err instanceof Error ? err.message : undefined,
        });
      })
      .finally(() => setPendingType(null));
  };

  const ruleByType = useMemo(
    () => new Map((ruleQuery.data ?? []).filter((r) => r.geofenceId === null).map((r) => [r.type, r])),
    [ruleQuery.data],
  );

  const handleLimitsSave = (
    item: (typeof ALERT_CATALOG)[number],
    config: AlertRuleConfig,
    actionKind?: "notify" | "block",
  ) => {
    setPendingType(item.key);
    ruleToggleMutation.mutate(
      { type: item.alertRuleType!, enabled: true, config, ...(actionKind ? { actionKind } : {}) },
      {
        onSuccess: () => toast.success("Limites salvos", { description: item.label }),
        onError: (err) =>
          toast.error("Não foi possível salvar os limites", {
            description: err instanceof Error ? err.message : undefined,
          }),
        onSettled: () => setPendingType(null),
      },
    );
  };

  const handleRuleToggle = (item: (typeof ALERT_CATALOG)[number], next: boolean) => {
    setPendingType(item.key);
    ruleToggleMutation.mutate(
      { type: item.alertRuleType!, enabled: next },
      {
        onSuccess: () => toast.success(next ? "Alerta ativado" : "Alerta desativado", { description: item.label }),
        onError: (err) =>
          toast.error("Não foi possível salvar o alerta", {
            description: err instanceof Error ? err.message : undefined,
          }),
        onSettled: () => setPendingType(null),
      },
    );
  };

  if (notifQuery.isLoading || ruleQuery.isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-12 w-full animate-pulse rounded-lg bg-slate-100" />
        ))}
      </div>
    );
  }
  if (notifQuery.isError || ruleQuery.isError) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-rose-600">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Não foi possível carregar os alertas deste veículo agora.
      </div>
    );
  }

  return (
    <div className="max-h-[420px] overflow-y-auto p-3">
      {(["meus-alertas", "avancados"] as const).map((groupKey) => (
        <div key={groupKey} className="mb-4 last:mb-0">
          <h3 className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            {groupKey === "meus-alertas" ? "Meus alertas" : "Alertas Avançados"}
          </h3>
          <ul className="space-y-1.5">
            {ALERT_CATALOG.filter((item) => item.group === groupKey).map((item) => {
              const togglable = !!item.alertRuleType || !!item.traccarTypes;
              const on = item.alertRuleType
                ? enabledRuleTypes.has(item.alertRuleType)
                : (item.traccarTypes?.every((t) => enabledTypes.has(t)) ?? false);
              const saving = item.alertRuleType
                ? pendingType === item.key && ruleToggleMutation.isPending
                : !!item.traccarTypes?.some((t) => pendingType === t) && toggleMutation.isPending;
              return (
                <li
                  key={item.key}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-sm">
                    {item.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-slate-800">{item.label}</div>
                    <div className="text-[11px] text-slate-500">
                      {togglable
                        ? on
                          ? ["Ativado", limitSummary(item.alertRuleType, ruleByType.get(item.alertRuleType!)?.config)]
                              .filter(Boolean)
                              .join(" · ")
                          : "Desativado"
                        : item.description}
                    </div>
                  </div>
                  {item.alertRuleType ? (
                    <Switch
                      checked={on}
                      disabled={saving}
                      onCheckedChange={(checked) => handleRuleToggle(item, checked)}
                      aria-label={item.label}
                    />
                  ) : item.traccarTypes ? (
                    <Switch
                      checked={on}
                      disabled={saving}
                      onCheckedChange={(checked) => handleToggle(item.traccarTypes!, checked)}
                      aria-label={item.label}
                    />
                  ) : (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500">
                      Em breve
                    </span>
                  )}
                  {on && isLimitEditable(item.alertRuleType) && (
                    <AlertLimitsEditor
                      key={JSON.stringify(ruleByType.get(item.alertRuleType!)?.config ?? null)}
                      type={item.alertRuleType!}
                      rule={ruleByType.get(item.alertRuleType!)}
                      saving={saving}
                      onSave={(config, actionKind) => handleLimitsSave(item, config, actionKind)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

// Mesmo gate de devices.view do mapa (ver routes/index.tsx#MapPageGate) —
// esta tabela mostra a frota inteira, tinha o mesmo buraco.
function VehiclesPageGate() {
  // Bug real 2026-09-01: "aparece sem permissões e depois de segundos
  // aparece" — ver usePermissionGateStatus.
  const status = usePermissionGateStatus("devices", "view");
  if (status === "loading") {
    return (
      <GpsInlineLoader className="h-[70vh]" />
    );
  }
  if (status === "denied") {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você não tem acesso para visualizar dispositivos. Peça a um administrador para
            conceder a permissão em Usuários → Editar → Permissões.
          </p>
        </div>
      </div>
    );
  }
  return <VehiclesPage />;
}

export const Route = createFileRoute("/vehicles")({
  component: VehiclesPageGate,
  validateSearch: (s: Record<string, unknown>) => ({ new: s.new === 1 || s.new === "1" ? 1 : undefined }),
  head: () => ({ meta: [{ title: "Veículos — Wp Rastreamento" }] }),
});

function formatConn(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.getDate().toString().padStart(2, "0");
  const month = d.toLocaleString("pt-BR", { month: "short" }).replace(".", "");
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  const monthCap = month.charAt(0).toUpperCase() + month.slice(1, 3);
  return `${day}/${monthCap} ${hh}:${mm}`;
}

// Pedido 2026-09-03: com a frota de teste de carga (~5000 devices) a tabela
// inteira renderizava sem paginação nenhuma — quebra em páginas de 20.
const PAGE_SIZE = 20;

function VehiclesPage() {
  const { data, isLoading, error } = useVehicles();
  // gpsDriveReady evita a mesma corrida de boot já corrigida em
  // routes/users.tsx (2026-09-03): sem isso, fotos/metadata disparavam
  // assim que a frota do Traccar chegava, sem esperar o access token do
  // GPS Drive Backend existir, tomavam 401 e só reapareciam certas depois
  // do retry — "aparece cache antigo e atualiza em segundos".
  const { gpsDriveStatus, gpsDriveUser } = useAuth();
  const gpsDriveReady = gpsDriveStatus !== "loading";
  // Fotos reais da frota — uma única chamada para todos os veículos da
  // página, nunca uma por linha (ver useVehicleImages).
  const { data: vehicleImages } = useVehicleImages((data ?? []).map((v) => v.id), gpsDriveReady);
  // Fornecedor/Simcard/Modelo dispositivo reais (GPS Drive Backend) — mesma
  // ideia em lote de useVehicleImages. Antes esta tabela mostrava valores
  // 100% inventados (hash determinístico do id, ver `detailsFor` removida
  // 2026-09-01 — bug relatado: "na lista de veiculo imei simcard fornecedor
  // chip e modelo dispositivo esta ficando ficticio"). IMEI nunca vem
  // daqui, sempre de v.imei (Traccar uniqueId, já real).
  const { data: metadataSummaries } = useVehicleMetadataSummaries(
    (data ?? []).map((v) => v.id),
    gpsDriveReady,
  );
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandTab, setExpandTab] = useState<ExpandTab>("comandos");
  const [accessFor, setAccessFor] = useState<Vehicle | null>(null);
  const [commandFor, setCommandFor] = useState<Vehicle | null>(null);
  const [editVehicle, setEditVehicle] = useState<Vehicle | null>(null);
  // Bug real 2026-09-01: mesmo incidente do menu de ações do mapa (ver
  // vehicle-actions-menu.tsx) — "Novo veículo"/"Editar" nesta tabela
  // (rota /vehicles, sem link no menu mas acessível direto pela URL para
  // qualquer usuário logado) também não checavam devices.create/edit.
  const canCreateDevices = usePermission("devices", "create");
  const canEditDevices = usePermission("devices", "edit");
  const handleExport = () => {
    if (!data) return;
    const headers = ["id", "name", "plate", "driver", "status", "speed", "lat", "lng", "lastConnection"];
    const rows = data.map((v) =>
      headers.map((h) => JSON.stringify((v as unknown as Record<string, unknown>)[h] ?? "")).join(","),
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `veiculos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  // "IMPORTAR" antigo só mostrava um alert sem cadastrar nada — trocado
  // por Criar em massa (pedido 2026-09-24).
  const [bulkOpen, setBulkOpen] = useState(false);
  // IMEI → id do dispositivo: a importação usa pra só vincular o cliente
  // quando o IMEI já existe (pedido 2026-09-30).
  const existingImeis = useMemo(
    () => new Map((data ?? []).filter((v) => !!v.imei).map((v) => [v.imei as string, v.id])),
    [data],
  );
  const search = Route.useSearch();
  const navigate = useNavigate();
  useEffect(() => {
    if (search.new === 1) {
      if (canCreateDevices) {
        setEditing(null);
        setOpen(true);
      }
      navigate({ to: "/vehicles", search: {} as never, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.new, canCreateDevices]);

  const openCreate = () => {
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (v: Vehicle) => {
    setEditVehicle(v);
  };


  // Busca por placa/IMEI/linha/ICCID/chassi/cliente (2026-10-01) — ver
  // lib/vehicle-search.ts. Cliente/linha vêm do índice do backend (só
  // equipe); sem ele, busca pelo que o Traccar já traz.
  const { data: searchIndex } = useVehicleSearchIndex(
    gpsDriveReady && !!gpsDriveUser && !isRestrictedAccountType(gpsDriveUser.accountType),
  );
  const haystacks = useMemo(
    () =>
      new Map(
        (data ?? []).map((v) => {
          const summary = metadataSummaries?.[v.id];
          const entry = searchIndex?.[v.id] ?? {
            clients: [],
            simNumber: summary?.simNumber ?? undefined,
          };
          return [v.id, vehicleSearchHaystack(v, entry)] as const;
        }),
      ),
    [data, searchIndex, metadataSummaries],
  );
  const filtered = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data;
    return data.filter((v) => {
      const h = haystacks.get(v.id);
      return !!h && matchesVehicleSearch(h, q);
    });
  }, [data, q, haystacks]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    setPage(1);
  }, [q]);
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);
  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  const allSelected = filtered.length > 0 && filtered.every((v) => selected.has(v.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((v) => v.id)));
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      {/* Top summary bar */}
      <div className="flex flex-wrap items-center gap-4 border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg bg-rose-50 text-lg">🚗</div>
          <div>
            <h1 className="text-base font-bold leading-tight text-slate-900">Veículos</h1>
            <p className="text-xs text-slate-500">Gerencie os veículos da sua frota</p>
          </div>
        </div>

        <div className="flex items-center gap-2 pl-3">
          <div className="text-right">
            <div className="text-2xl font-bold leading-none text-sky-500">{data?.length ?? 0}</div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Dispositivos
            </div>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {canCreateDevices && (
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              ⬆ CRIAR EM MASSA
            </button>
          )}
          <VehicleBulkCreateDialog open={bulkOpen} onOpenChange={setBulkOpen} existingImeis={existingImeis} />
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            ⬇ EXPORTAR
          </button>
          <VehicleTrashDialog />
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔎</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Placa, IMEI, linha, cliente..."
              className="h-9 w-64 rounded-full border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300"
            />
          </div>
          <button
            type="button"
            onClick={openCreate}
            disabled={!canCreateDevices}
            aria-label="Novo veículo"
            title={canCreateDevices ? undefined : "Sem permissão"}
            className="grid h-9 w-9 place-items-center rounded-full bg-sky-500 text-white shadow-sm hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            +
          </button>
        </div>
      </div>

      {isLoading || error || !data ? (
        <div className="p-6">
          <WaitingBackend error={error} />
        </div>
      ) : (
        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="w-12 px-3 py-3">
                    <div className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <span className="text-slate-400">⚙</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left">Veículo</th>
                  <th className="px-3 py-3 text-left">IMEI</th>
                  <th className="px-3 py-3 text-left">Simcard</th>
                  <th className="px-3 py-3 text-left">Fornecedor chip</th>
                  <th className="px-3 py-3 text-left">Modelo dispositivo</th>
                  <th className="px-3 py-3 text-left">Conexão</th>
                  <th className="px-3 py-3 text-left">Editar</th>
                  <th className="w-10 px-3 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paged.map((v) => {
                  const on = selected.has(v.id);
                  const icon = resolveVehicleIcon(v);
                  const imageUrls = vehicleImages?.[v.id];
                  // Foto real cadastrada tem prioridade sobre o ícone genérico
                  // — variante "128" é suficiente para o avatar de 36px, nunca
                  // "original". Sem foto, cai no ícone de sempre.
                  const photoUrl = imageUrls
                    ? resolveGpsDriveUrl(imageUrls["128"] ?? imageUrls.original)
                    : undefined;
                  const summary = metadataSummaries?.[v.id];
                  const dot =
                    v.status === "online"
                      ? "bg-emerald-500"
                      : v.status === "idle"
                        ? "bg-amber-500"
                        : "bg-rose-500";
                  const isExpanded = expandedId === v.id;
                  return (
                    <Fragment key={v.id}>
                      <tr className={cn("hover:bg-slate-50", on && "bg-sky-50/40", isExpanded && "bg-slate-50")}>
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleOne(v.id)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-3">
                          <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
                            <img
                              src={photoUrl ?? icon.url}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                            <span
                              className={cn(
                                "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white",
                                dot,
                              )}
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-900">
                              <span className="truncate">
                                <LicensePlate plate={v.plate} size="xs" className="mr-1.5" />{(v.driver ?? v.name).toUpperCase()}
                              </span>
                              <span className="text-sky-500">↗</span>
                            </div>
                            <div className="truncate text-[11px] text-slate-500">
                              <LicensePlate plate={v.plate} size="xs" className="mr-1.5" /><span className="text-sky-600">{v.name}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{v.imei ?? "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">
                        {summary?.simNumber ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-xs font-semibold text-slate-700">
                        {/* Fornecedor quase nunca é preenchido; a operadora
                            (simCarrier) é o que identifica o chip na prática. */}
                        {[summary?.trackerSupplier, summary?.simCarrier].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-700">
                        {summary?.trackerModel ?? summary?.trackerBrand ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">
                        {formatConn(v.lastConnection)}
                        <div className="text-[10.5px] text-slate-400">{agoLabel(v.lastConnection)}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => openEdit(v)}
                          disabled={!canEditDevices}
                          aria-label="Editar"
                          title={canEditDevices ? undefined : "Sem permissão"}
                          className="grid h-8 w-8 place-items-center rounded-md bg-sky-500 text-white shadow-sm hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          ✎
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-slate-400">
                        <button
                          type="button"
                          aria-label={isExpanded ? "Recolher" : "Expandir"}
                          onClick={() => setExpandedId(isExpanded ? null : v.id)}
                          className={cn(
                            "grid h-6 w-6 place-items-center rounded-md transition-transform hover:bg-slate-100",
                            isExpanded && "rotate-180 text-sky-500",
                          )}
                        >
                          ⌄
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} className="bg-white p-0">
                          <div className="border-y border-slate-200">
                            {/* Tab pills */}
                            <div className="flex flex-wrap items-center gap-1.5 bg-slate-100/60 px-3 py-2">
                              {(
                                [
                                  { k: "acessos", label: "Acessos" },
                                  { k: "comandos", label: "Enviar comandos" },
                                  { k: "logs", label: "Logs" },
                                  { k: "alertas", label: "Alertas" },
                                  { k: "relatorios", label: "Relatórios" },
                                ] as { k: ExpandTab; label: string }[]
                              ).map((t) => {
                                const active = expandTab === t.k;
                                return (
                                  <button
                                    key={t.k}
                                    onClick={() => {
                                      setExpandTab(t.k);
                                      if (t.k === "acessos") setAccessFor(v);
                                      else if (t.k === "comandos") setCommandFor(v);
                                    }}
                                    className={cn(
                                      "rounded-md border px-3 py-1 text-[10px] font-bold uppercase tracking-wider",
                                      active
                                        ? "border-sky-400 bg-white text-sky-600 shadow-sm"
                                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50",
                                    )}
                                  >
                                    {t.label}
                                  </button>
                                );
                              })}
                            </div>
                            {/* Logs/Alertas trocam o conteúdo pelo painel
                                real correspondente; as demais abas mantêm o
                                mapa ao vivo por baixo (Acessos/Comandos
                                abrem um diálogo por cima, ver onClick acima). */}
                            {expandTab === "logs" ? (
                              <VehicleLogsPanel vehicle={v} />
                            ) : expandTab === "alertas" ? (
                              <VehicleAlertsPanel vehicle={v} />
                            ) : (
                              <VehicleExpandedMap vehicle={v} />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-sm text-slate-500">
                      Nenhum veículo encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de{" "}
                {filtered.length} veículo{filtered.length === 1 ? "" : "s"}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ← Anterior
                </button>
                <span className="text-xs font-semibold text-slate-600">
                  Página {page} de {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Próxima →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        aria-label="Configurações"
        className="fixed bottom-6 right-6 grid h-11 w-11 place-items-center rounded-full bg-sky-500 text-white shadow-lg hover:bg-sky-600"
      >
        ⚙
      </button>

      <VehicleFormDialog open={open} onOpenChange={setOpen} editing={editing} />
      <VehicleAccessDialog open={!!accessFor} onOpenChange={(o) => !o && setAccessFor(null)} vehicle={accessFor} />
      <VehicleSendCommandDialog open={!!commandFor} onOpenChange={(o) => !o && setCommandFor(null)} vehicle={commandFor} />
      <VehicleEditDialog vehicle={editVehicle} onOpenChange={(o) => !o && setEditVehicle(null)} />
    </div>
  );
}
